import { cloneDeep } from 'lodash';
import { AfterViewInit, Component, OnDestroy, OnInit } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { Subscription } from 'rxjs';

import {
    AddParticipantUseCase,
    CheckClientUseCase,
    ClientRelation,
    ClientType,
    GetFirstHolderUseCase,
    GetParticipantsUseCase,
    GetTutorsUseCase,
    RemoveParticipantUseCase,
    UpdateParticipantUseCase,
} from '../../../../core';
import { BasePage } from '../../base.page';
import { Client } from '../../../domain/data';
import { ContractPage } from '../contract.page';
import { ErrorDialog } from '../../../ui/dialog/error/error.dialog';
import { ErrorAddingParticipantDialog } from '../../../ui/dialog/error-adding-participant/error-adding-participant.dialog';
import { MainRoutes } from '../../../main-routes.enum';
import { ParentEvents } from '../../../utils/parent-events.service';

import { environment } from '../../../../../environments/environment';

@Component({
    templateUrl: './contract-participants.page.html',
    styleUrls: ['./contract-participants.page.scss'],
})
export class ContractParticipantsPage extends BasePage implements AfterViewInit, OnInit, OnDestroy {
    public ClientRelation: typeof ClientRelation = ClientRelation;
    public ClientType: typeof ClientType = ClientType;

    public participants: Client[] = [];
    public tutors: Client[] = [];
    public isAddingParticipant = false;
    public isFirst = false;
    public isLegalPerson = false;
    public editClient: Client;
    public errorMsg: string;
    public isClientPortal = environment.portalClient;

    private backSubscription: Subscription;

    constructor(
        contractPage: ContractPage,
        parentEvents: ParentEvents,
        private addParticipantUseCase: AddParticipantUseCase,
        private checkClientUseCase: CheckClientUseCase,
        private dialog: MatDialog,
        private getFirstHolderUseCase: GetFirstHolderUseCase,
        private getParticipantsUseCase: GetParticipantsUseCase,
        private getTutorsUseCase: GetTutorsUseCase,
        private router: Router,
        private removeParticipantUseCase: RemoveParticipantUseCase,
        private updateParticipantUseCase: UpdateParticipantUseCase
    ) {
        super(parentEvents);
        contractPage.showBackButton();
        this.backSubscription = contractPage.back.subscribe(() => {
            if (this.participants.some((participant) => participant.preload)) {
                this.parentEvents.cancel();
            } else {
                window.history.back();
            }
        });
    }

    ngOnInit() {
        this.getFirstHolder();
        this.getParticipants();
        this.getTutors();
    }

    private getFirstHolder() {
        this.getFirstHolderUseCase.bind().subscribe((transaction) => {
            if (transaction.isSuccess) {
                this.isLegalPerson = transaction.data.isLegalPerson;
            } else if (!transaction.isAuthError) {
                this.dialog.open(ErrorDialog);
            }
        });
    }

    private getParticipants() {
        this.getParticipantsUseCase.bind({ isFull: false }).subscribe((transaction) => {
            if (transaction.isSuccess) {
                this.participants = transaction.data as Client[];
            } else if (!transaction.isAuthError) {
                this.dialog.open(ErrorDialog);
            }
        });
    }

    private getTutors() {
        this.getTutorsUseCase.bind().subscribe((transaction) => {
            if (transaction.isSuccess) {
                this.tutors = transaction.data as Client[];
            } else if (!transaction.isAuthError) {
                this.dialog.open(ErrorDialog);
            }
        });
    }

    ngOnDestroy() {
        this.backSubscription.unsubscribe();
    }

    onAddParticipant() {
        this.errorMsg = null;
        this.isFirst = false;
        this.isAddingParticipant = true;
    }

    onCancel() {
        this.editClient = null;
        this.isAddingParticipant = false;
    }

    onCompleteParticipant(client: Client) {
        if (this.isClientPortal) {
            this.checkClientUseCase
                .bind({
                    client,
                    captcha: client.recaptchaToken,
                })
                .subscribe((transaction) => {
                    if (transaction.isSuccess) {
                        this.addOrUpdate(client);
                    } else if (!transaction.isAuthError) {
                        this.dialog.open(ErrorAddingParticipantDialog, {
                            data: transaction.error,
                        });
                    }
                });
        } else {
            this.addOrUpdate(client);
        }
    }

    private addOrUpdate(client: Client) {
        if (this.editClient) {
            this.updateParticipantUseCase.bind({ client }).subscribe((transaction) => {
                if (transaction.isSuccess) {
                    this.editClient = null;
                    this.isAddingParticipant = false;
                    this.getParticipants();
                    this.getTutors();
                } else if (!transaction.isAuthError) {
                    this.dialog.open(ErrorDialog);
                }
            });
        } else {
            this.addParticipantUseCase.bind({ client }).subscribe((transaction) => {
                if (transaction.isSuccess) {
                    this.isAddingParticipant = false;
                    this.getParticipants();
                    this.getTutors();
                } else if (!transaction.isAuthError) {
                    this.dialog.open(ErrorDialog);
                }
            });
        }
    }

    onEdit(client: Client, isFirst = false) {
        this.errorMsg = null;
        this.editClient = cloneDeep(client);
        this.isFirst = isFirst;
        this.isAddingParticipant = true;
    }

    onRemove(client: Client) {
        this.errorMsg = null;
        this.removeParticipantUseCase.bind({ client }).subscribe((transaction) => {
            if (transaction.isSuccess) {
                this.getParticipants();
                this.getTutors();
            } else {
                this.errorMsg = transaction.error;
            }
        });
    }

    onSubmit() {
        this.router.navigateByUrl(MainRoutes.NEW_CONTRACT_SIGN);
    }
}
